// An ad copy's ten text sections, in live's order with live's labels and icons (`TCS_TEXTS`,
// `constants/images.tsx`), and what a copy or a structure holds of them (G8).
import type { LucideIcon } from "lucide-react";

import type { AdCopyElements, AdCopyTextElements } from "../../../api/generated/model";
import { TEXT_ELEMENTS, type TextKey } from "../../../platform/media/structure";

/** A section's key, as G8 names it (live's `text_elements` keys). */
export type { TextKey } from "../../../platform/media/structure";

export interface Section {
  key: TextKey;
  label: string;
  icon: LucideIcon;
}

/** Live's ten sections, in its order: a structure's texts (`platform/media/structure.ts`). */
export const SECTIONS: readonly Section[] = TEXT_ELEMENTS.map(({ key, label, icon }) => ({ key, label, icon }));

export function sectionOf(key: TextKey): Section {
  return SECTIONS.find((section) => section.key === key)!;
}

/** A copy's values for a section, without the blank ones (live's `getNonEmptyValues`). */
export function valuesOf(elements: AdCopyElements, key: TextKey): string[] {
  return (elements[key] ?? []).filter((value) => value.trim() !== "");
}

/** The sections a copy has values in, in live's order. */
export function sectionsOf(elements: AdCopyElements): TextKey[] {
  return SECTIONS.map((section) => section.key).filter((key) => valuesOf(elements, key).length > 0);
}

/** A copy's counts, live's badges: its sections, and their values (`getCreativeStructure...Count`). */
export function countsOf(elements: AdCopyElements): { sections: number; elements: number } {
  const keys = sectionsOf(elements);
  return {
    sections: keys.length,
    elements: keys.reduce((sum, key) => sum + valuesOf(elements, key).length, 0),
  };
}

/** Whether an analysed inspiration has the section (live's `hasNonEmptyValues`). */
export function structureHas(structure: AdCopyTextElements, key: TextKey): boolean {
  return (structure[key] ?? "").trim() !== "";
}
